import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import { addDisplayMinutes } from 'owa-datetime-utils/lib/addDisplayMinutes';
import { ColumnIndex } from '../interfaces/ColumnRange';
import { ColumnProvider } from '../interfaces/ColumnProvider';
import { DisplayDate } from 'owa-datetime';
import { getColumn } from './getColumn';
import { Offset } from '../interfaces/LayoutInterval';
import { VisibleTime } from 'owa-calendar-surface-core';

/**
 * Gets the offset that represents a given date in a given column.
 * @param date The date & time which we want to convert.
 * @param columnIndex The index of the column in which we want to show the respective interval.
 * @param store Settings from the calendar surface store
 *
 * Note: the returned offset is not necessarily unique for the given
 * date if the time occurs outside the visible window, represented by
 * dayStartMinute and dayLength.
 *
 * Also note that the same date can be displayed in different columns,
 * such as when showing multiple calendars or people as columns.
 */
export function getOffset(date: DisplayDate, columnIndex: ColumnIndex, store: VisibleTime): Offset {
    const columnStart = columnIndex * store.dayLength;
    const minutes =
        date.getHours() * TimeConstants.MinutesInOneHour + date.getMinutes() - store.dayStartMinute;
    return columnStart + minutes;
}

/**
 * Gets the date represented by a given offset.
 * @param offset The offset to be converted to a date.
 * @param store Settings from the calendar surface store
 * @param columnProvider A column provider to convert a column index to a date.
 *
 * This function mandates that offsets must represent minutes,
 * each column must have a total of 'dayLength' offsets and the
 * column provider must provide a date that represents the start
 * of the day that each column represents, regardless if it is a
 * 'day'-column or something else, such as calendar or people.
 * The final time is also offset by the 'dayStartMinute'.
 */
export function getDate(
    offset: Offset,
    store: VisibleTime,
    columnProvider: ColumnProvider,
    endDate: boolean = false
): DisplayDate {
    const columnIndex = getColumn(offset, store.dayLength);
    const columnDate = columnProvider.getColumnDate(columnIndex);
    const minutes = store.dayStartMinute + (offset % store.dayLength);

    // VSO 29566: "end" of a column, or minutes = 0, when selecting an end date, means the end of the current column
    // but the date is midnight of the next column, which is not necessarily the start of the next day (work week, etc).
    // so add 24 hours to beginning of current column to get correct end date
    if (minutes == 0 && endDate) {
        return addDisplayMinutes(columnProvider.getColumnDate(columnIndex - 1), store.dayLength);
    } else {
        return addDisplayMinutes(columnDate, minutes);
    }
}
